import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { range, sum, values } from 'lodash-es';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import LinearProgress from '@mui/material/LinearProgress';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Link from '@mui/material/Link';
import { useMatch } from '../../contexts/MatchContext';
import * as api from '../../api';
import { getScoresWithPoints, numShotsPerPaper, pad, getFlagSrc, getAllDivisions } from '../../utils/scoring';

function StrikeThroughNewValue({ value, newValue }) {
  if (newValue === null || newValue === undefined || value === newValue) return <>{value}</>;
  return (
    <span>
      <span style={{ textDecoration: 'line-through', opacity: 0.5 }}>{value}</span>
      {' '}
      <span style={{ fontWeight: 'bold', color: '#d32f2f' }}>{newValue}</span>
    </span>
  );
}

function WhatIfDialog({ t, stageIndex, rawScore, stage, onClose, onReset, onChangeScore, scoreMode }) {
  const [time, setTime] = useState(rawScore?.time || 0);

  useEffect(() => {
    setTime(rawScore?.time || 0);
  }, [rawScore?.time]);

  if (!rawScore || !stage) return null;

  const targets = stage.targets || {};
  const metal = (targets.poppers || 0) + (targets.minipoppers || 0) + (targets.plates || 0) + (targets['pcc-frangible'] || 0) + (targets['pcc-ten-points-poppers'] || 0) + (targets['pcc-ten-points-frangible'] || 0);
  const paper = (targets.paper || 0) + (targets.minipaper || 0);
  const maxShots = paper > 0 ? numShotsPerPaper({ stage }) : 0;
  const maxNoShoots = ((targets['paper-no-shoots'] || 0) * 2) + (targets['metal-no-shoots'] || 0);
  const penalties = sum(values(rawScore.penaltiesByType)) || 0;

  const possibleScoresForTarget = (targetIdx) => {
    const ONE = ((stage.bonusTargets || []).indexOf(targetIdx) > -1) ? ['A', 'C', 'D', 'm'] : ['A', 'C', 'D', 'M'];
    if (maxShots <= 1) return ONE;
    if (maxShots === 2) {
      const r = [];
      ONE.forEach((s, i) => ONE.slice(i).forEach((s2) => r.push(`${s}${s2}`)));
      return r;
    }
    if (maxShots === 3) {
      const r = [];
      ONE.forEach((s, i) => ONE.slice(i).forEach((s2, j) => ONE.slice(i + j).forEach((s3) => r.push(`${s}${s2}${s3}`))));
      return r;
    }
    return ONE;
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {t('results.stageN', { n: stageIndex + 1 })} – {t('results.whatIf')}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
          <Typography variant="body2">{t('results.time')}:</Typography>
          <TextField
            size="small"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            onBlur={() => { if (parseFloat(time)) onChangeScore({ time: parseFloat(time) }); }}
            sx={{ width: 100 }}
          />
        </Box>

        {metal > 0 && (
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2">{t('results.poppers')}:</Typography>
            <Select
              size="small"
              value={rawScore.poppers ?? 0}
              onChange={(e) => onChangeScore({ poppers: e.target.value })}
              sx={{ minWidth: 150 }}
            >
              {range(metal + 1).map((m) => (
                <MenuItem key={m} value={metal - m}>
                  {t('results.whatIfMetals', { hit: metal - m, miss: m })}
                </MenuItem>
              ))}
            </Select>
          </Box>
        )}

        {paper > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>{t('scoring.targets')}:</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {range(paper).map((p) => (
                <Box key={p} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="caption">T{p + 1}</Typography>
                  <Select
                    size="small"
                    value={(rawScore.scorePerTarget || [])[p] || ''}
                    onChange={(e) => {
                      const next = [...(rawScore.scorePerTarget || [])];
                      next[p] = e.target.value;
                      onChangeScore({ scorePerTarget: next });
                    }}
                    sx={{ minWidth: 70 }}
                  >
                    {possibleScoresForTarget(p)?.map((m) => (
                      <MenuItem key={m} value={m}>
                        {scoreMode === 'comstock-abd' ? m.replace(/C/g, 'B') : m}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {maxNoShoots > 0 && (
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2">{t('results.noShoots')}:</Typography>
            <Select
              size="small"
              value={rawScore['no-shoots'] || 0}
              onChange={(e) => onChangeScore({ 'no-shoots': e.target.value })}
              sx={{ minWidth: 60 }}
            >
              {range(maxNoShoots + 1).map((m) => (
                <MenuItem key={m} value={m}>{m}</MenuItem>
              ))}
            </Select>
          </Box>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2">{t('results.penalties')}:</Typography>
          <Select
            size="small"
            value={penalties}
            onChange={(e) => onChangeScore({ penaltiesByType: { general: e.target.value } })}
            sx={{ minWidth: 60 }}
          >
            {range(20).map((m) => (
              <MenuItem key={m} value={m}>{m}</MenuItem>
            ))}
          </Select>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onReset}>{t('results.whatIfReset')}</Button>
        <Button onClick={onClose}>{t('common.close')}</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function ShooterStats() {
  const { t } = useTranslation();
  const { match } = useMatch();
  const { shooterId: routeShooterId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [shooters, setShooters] = useState(null);
  const [scores, setScores] = useState(null);
  const [stages, setStages] = useState(null);
  const [selectedShooter, setSelectedShooter] = useState(null);
  const [whatIfStageIdx, setWhatIfStageIdx] = useState(null);

  // Restore whatIf from query params (when navigating back from score-table)
  const whatIfParam = searchParams.get('whatIf');
  const whatIfOverrideParam = searchParams.get('whatIfOverride');
  const [whatIfScores, setWhatIfScores] = useState(() => {
    if (!whatIfParam) return [];
    try { return JSON.parse(decodeURIComponent(escape(atob(whatIfParam)))); } catch { return []; }
  });
  const initialOverride = useMemo(() => {
    if (!whatIfOverrideParam) return null;
    try { return JSON.parse(decodeURIComponent(escape(atob(whatIfOverrideParam)))); } catch { return null; }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [whatIfDivision, setWhatIfDivision] = useState(initialOverride?.division || null);
  const [whatIfPowerFactor, setWhatIfPowerFactor] = useState(initialOverride?.powerFactor || null);

  // Clean whatIf params from URL after restoring
  useEffect(() => {
    if (whatIfParam || whatIfOverrideParam) {
      const next = new URLSearchParams(searchParams);
      next.delete('whatIf');
      next.delete('whatIfOverride');
      setSearchParams(next, { replace: true });
    }
  }, []);

  useEffect(() => {
    if (!match) return;
    window.scrollTo(0, 0);

    Promise.all([
      api.getShooters(match.id),
      api.getScores(match.id),
      match.stages?.length > 0 ? api.getStages(match.stages.join(',')) : Promise.resolve({ data: [] }),
    ]).then(([shootersRes, scoresRes, stagesRes]) => {
      const allShooters = (shootersRes.data || []).filter((s) => !s.cancelledParticipation);
      setShooters(allShooters);
      setScores(scoresRes.data?.scores || scoresRes.data || []);
      setStages(stagesRes.data || []);

      if (routeShooterId) {
        const found = allShooters.find((s) => s.id === routeShooterId);
        if (found) setSelectedShooter(found);
      }
    });
  }, [match?.id]);

  const overrideShooters = (sList) => {
    if (!sList || !selectedShooter || (!whatIfDivision && !whatIfPowerFactor)) return sList;
    return sList.map((s) => {
      if (s.id !== selectedShooter.id) return s;
      return {
        ...s,
        ...(whatIfDivision ? { division: whatIfDivision } : {}),
        ...(whatIfPowerFactor ? { 'power-factor': whatIfPowerFactor } : {}),
      };
    });
  };

  const hasOverride = !!(whatIfDivision || whatIfPowerFactor);

  const { scoresByStage, scoresOverall } = useMemo(() => {
    if (!scores || !shooters || !stages) return {};
    return getScoresWithPoints({
      scores, shooters, stages, combined: true, mode: match.scoreMode,
    });
  }, [scores, shooters, stages]);

  const { scoresByStage: wiScoresByStage, scoresOverall: wiScoresOverall } = useMemo(() => {
    if (!scores || !shooters || !stages || (whatIfScores.length === 0 && !hasOverride)) {
      return { scoresByStage, scoresOverall };
    }
    try {
      return getScoresWithPoints({
        scores: [...scores, ...whatIfScores], shooters: overrideShooters(shooters), stages, combined: true, mode: match.scoreMode,
      });
    } catch (e) {
      console.error('WhatIf score computation failed:', e);
      return { scoresByStage, scoresOverall };
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scores, shooters, stages, whatIfScores, scoresByStage, scoresOverall, whatIfDivision, whatIfPowerFactor, selectedShooter?.id]);

  const scoreMatchesShooter = (s, shooter) => {
    if (!s || !shooter) return false;
    return s.shooterId?.toString() === shooter.id?.toString()
      || `${s.matchId}_${s.shooterId}` === shooter.id;
  };

  const getLatestRawScore = (stageIdx) => {
    if (!selectedShooter || !scores) return null;
    const allScores = [...scores, ...whatIfScores];
    const matching = allScores.filter((s) =>
      s.stageIdx === stageIdx + 1 && !s.deleted && scoreMatchesShooter(s, selectedShooter)
    );
    return matching.reduce((latest, s) => (!latest || s.timestamp > latest.timestamp) ? s : latest, null);
  };

  const handleChangeScore = (stageIdx, change) => {
    const rawScore = getLatestRawScore(stageIdx);
    if (!rawScore) return;
    setWhatIfScores([
      ...whatIfScores.filter((s) => !(s.stageIdx === stageIdx + 1 && scoreMatchesShooter(s, selectedShooter))),
      { ...rawScore, ...change, timestamp: (rawScore.timestamp || 0) + 1 },
    ]);
  };

  const handleResetStage = (stageIdx) => {
    setWhatIfScores(whatIfScores.filter((s) => !(s.stageIdx === stageIdx + 1 && scoreMatchesShooter(s, selectedShooter))));
    setWhatIfStageIdx(null);
  };

  const handleShooterChange = (_, v) => {
    setSelectedShooter(v);
    setWhatIfScores([]);
    setWhatIfStageIdx(null);
    setWhatIfDivision(null);
    setWhatIfPowerFactor(null);
  };

  if (!shooters || !scores || !stages) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  const getShooterLabel = (shooter) => {
    if (!shooter) return '';
    const parts = (shooter.publicId || '').split('|');
    const displayId = parts[parts.length - 1];
    return `${displayId} - ${shooter.name} (${shooter.club || ''})`;
  };

  const shooterStageScores = selectedShooter && wiScoresByStage
    ? wiScoresByStage.map((stageScores) => stageScores.find((s) => s.shooterId === selectedShooter.id))
    : [];

  const origShooterStageScores = selectedShooter && scoresByStage
    ? scoresByStage.map((stageScores) => stageScores.find((s) => s.shooterId === selectedShooter.id))
    : [];

  const shooterOverall = selectedShooter && wiScoresOverall
    ? wiScoresOverall.find((s) => s.shooterId === selectedShooter.id)
    : null;

  const origShooterOverall = selectedShooter && scoresOverall
    ? scoresOverall.find((s) => s.shooterId === selectedShooter.id)
    : null;

  const hasWhatIf = whatIfScores.length > 0 || hasOverride;

  const isStageModified = (idx) => {
    const orig = origShooterStageScores[idx];
    const wi = shooterStageScores[idx];
    if (!orig || !wi || !hasWhatIf) return false;
    return orig.alphas !== wi.alphas || orig.charlies !== wi.charlies || orig.deltas !== wi.deltas
      || orig.misses !== wi.misses || orig.noShoots !== wi.noShoots || orig.penalties !== wi.penalties
      || orig.time !== wi.time || orig.total !== wi.total;
  };

  const totalAlphas = shooterStageScores.reduce((s, sc) => s + (sc?.alphas || 0), 0);
  const totalCharlies = shooterStageScores.reduce((s, sc) => s + (sc?.charlies || 0), 0);
  const totalDeltas = shooterStageScores.reduce((s, sc) => s + (sc?.deltas || 0), 0);
  const totalMisses = shooterStageScores.reduce((s, sc) => s + (sc?.misses || 0), 0);
  const totalNoShoots = shooterStageScores.reduce((s, sc) => s + (sc?.noShoots || 0), 0);
  const totalPenalties = shooterStageScores.reduce((s, sc) => s + (sc?.penalties || 0), 0);
  const totalHits = totalAlphas + totalCharlies + totalDeltas;

  const toDivisionKey = (div) => {
    const map = {
      'standard': 'standard', 'production': 'production', 'open': 'open',
      'production-optics': 'productionOptics', 'optics': 'optics', 'classic': 'classic',
      'revolver': 'revolver', 'pcc-optics': 'pccOptics', 'pcc-iron': 'pccIron',
    };
    return map[div] || div;
  };

  const toCategoryKey = (cat) => {
    const map = {
      'regular': 'regular', 'senior': 'senior', 'super-senior': 'superSenior',
      'grand-senior': 'grandSenior', 'lady-senior': 'ladySenior', 'junior': 'junior',
      'super-junior': 'superJunior', 'grand-junior': 'grandJunior',
    };
    return map[cat] || cat;
  };

  const renderCell = (orig, wi, field, formatter) => {
    const origVal = orig ? (formatter ? formatter(orig[field]) : orig[field]) : null;
    const wiVal = wi ? (formatter ? formatter(wi[field]) : wi[field]) : null;
    if (!hasWhatIf || origVal === wiVal) return wiVal;
    return <StrikeThroughNewValue value={origVal} newValue={wiVal} />;
  };

  const currentRawScore = whatIfStageIdx !== null ? getLatestRawScore(whatIfStageIdx) : null;

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', px: 2 }}>
      <Autocomplete
        options={shooters}
        getOptionLabel={getShooterLabel}
        value={selectedShooter}
        onChange={handleShooterChange}
        renderInput={(params) => <TextField {...params} label={t('results.selectShooter')} />}
        sx={{ mb: 3 }}
        isOptionEqualToValue={(o, v) => o?.id === v?.id}
      />

      {selectedShooter && (
        <>
          {/* Shooter Info */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              {selectedShooter.publicId?.includes('|') && (
                <img
                  alt={selectedShooter.publicId.split('|')[0]}
                  src={getFlagSrc({ countryCode: selectedShooter.publicId.split('|')[0], size: 24 })}
                  style={{ display: 'block' }}
                />
              )}
              <Typography variant="h6">{selectedShooter.name}</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              <Box>
                <Typography variant="caption" color="text.secondary">{t('shooter.club')}</Typography>
                <Typography variant="body2">{selectedShooter.club || '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">{t('shooter.division')}</Typography>
                <Select
                  size="small"
                  variant="standard"
                  value={whatIfDivision || selectedShooter.division}
                  onChange={(e) => setWhatIfDivision(e.target.value === selectedShooter.division ? null : e.target.value)}
                  sx={{
                    fontSize: '0.875rem',
                    color: whatIfDivision ? '#d32f2f' : 'inherit',
                    fontWeight: whatIfDivision ? 'bold' : 'normal',
                  }}
                >
                  {getAllDivisions({ matchType: match.type }).map((d) => (
                    <MenuItem key={d} value={d}>{t(`divisions.${toDivisionKey(d)}`, { defaultValue: d })}</MenuItem>
                  ))}
                </Select>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">{t('shooter.category')}</Typography>
                <Typography variant="body2">{t(`categories.${toCategoryKey(selectedShooter.category)}`, { defaultValue: selectedShooter.category })}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">{t('shooter.powerFactor')}</Typography>
                <Select
                  size="small"
                  variant="standard"
                  value={whatIfPowerFactor || selectedShooter['power-factor'] || 'minor'}
                  onChange={(e) => setWhatIfPowerFactor(e.target.value === (selectedShooter['power-factor'] || 'minor') ? null : e.target.value)}
                  sx={{
                    fontSize: '0.875rem',
                    color: whatIfPowerFactor ? '#d32f2f' : 'inherit',
                    fontWeight: whatIfPowerFactor ? 'bold' : 'normal',
                  }}
                >
                  <MenuItem value="minor">{t('registration.minor')}</MenuItem>
                  <MenuItem value="major">{t('registration.major')}</MenuItem>
                </Select>
              </Box>
              {shooterOverall && (() => {
                // Accuracy: (shooterHitValue / bestHitValue) per stage, averaged
                const hitValue = (s) => (s?.alphas || 0) * 10 + (s?.charlies || 0) * 8 + (s?.deltas || 0) * 3;
                const accuracies = (wiScoresByStage || []).map((stageScores, idx) => {
                  const shooter = stageScores.find((s) => s.shooterId === selectedShooter.id);
                  if (!shooter || !shooter.time) return null;
                  const best = stageScores.reduce((b, s) => (!s.time ? b : hitValue(s) > hitValue(b) ? s : b), stageScores[0]);
                  const bestVal = hitValue(best);
                  return bestVal > 0 ? hitValue(shooter) / bestVal : null;
                }).filter((v) => v !== null);
                const accuracyScore = accuracies.length > 0 ? Math.floor(accuracies.reduce((a, b) => a + b, 0) / accuracies.length * 100) : null;

                // Speed: (worstTime - shooterTime) / (worstTime - bestTime) per stage, averaged
                const speeds = (wiScoresByStage || []).map((stageScores) => {
                  const shooter = stageScores.find((s) => s.shooterId === selectedShooter.id);
                  if (!shooter || !shooter.time) return null;
                  const times = stageScores.filter((s) => s.time > 0).map((s) => s.time);
                  if (times.length < 2) return null;
                  const best = Math.min(...times);
                  const worst = Math.max(...times);
                  return worst === best ? 1 : (worst - shooter.time) / (worst - best);
                }).filter((v) => v !== null);
                const speedScore = speeds.length > 0 ? Math.floor(speeds.reduce((a, b) => a + b, 0) / speeds.length * 100) : null;

                return <>
                  <Box>
                    <Typography variant="caption" color="text.secondary">{t('results.totalPoints')}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {hasWhatIf && origShooterOverall && pad(origShooterOverall.score, 4) !== pad(shooterOverall.score, 4)
                        ? <StrikeThroughNewValue value={pad(origShooterOverall.score, 4)} newValue={pad(shooterOverall.score, 4)} />
                        : pad(shooterOverall.score, 4)
                      }
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">{t('results.overallPercent')}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {hasWhatIf && origShooterOverall && pad(origShooterOverall.percentage / 100, 2) !== pad(shooterOverall.percentage / 100, 2)
                        ? <StrikeThroughNewValue value={`${pad(origShooterOverall.percentage / 100, 2)}%`} newValue={`${pad(shooterOverall.percentage / 100, 2)}%`} />
                        : `${pad(shooterOverall.percentage / 100, 2)}%`
                      }
                    </Typography>
                  </Box>
                  {accuracyScore !== null && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">{t('results.accuracyScore')}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{accuracyScore}%</Typography>
                    </Box>
                  )}
                  {speedScore !== null && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">{t('results.speedScore')}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{speedScore}%</Typography>
                    </Box>
                  )}
                </>;
              })()}
            </Box>
          </Paper>

          {/* Hit Summary */}
          {totalHits > 0 && (
            <Paper sx={{ p: 2, mb: 3 }}>
              <Typography variant="h6" gutterBottom>{t('results.hitSummary')}</Typography>
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 2 }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="success.main">{totalAlphas}</Typography>
                  <Typography variant="caption">{t('results.alphas')}</Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="info.main">{totalCharlies}</Typography>
                  <Typography variant="caption">{t('results.charlies')}</Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="warning.main">{totalDeltas}</Typography>
                  <Typography variant="caption">{t('results.deltas')}</Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="error.main">{totalMisses}</Typography>
                  <Typography variant="caption">{t('results.misses')}</Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="error.main">{totalNoShoots}</Typography>
                  <Typography variant="caption">{t('results.noShoots')}</Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="error.main">{totalPenalties}</Typography>
                  <Typography variant="caption">{t('results.penalties')}</Typography>
                </Box>
              </Box>

              {totalHits > 0 && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t('results.alphaPercent', { pct: Math.round((totalAlphas / totalHits) * 100) })}
                    {' | '}
                    {t('results.charliePercent', { pct: Math.round((totalCharlies / totalHits) * 100) })}
                    {' | '}
                    {t('results.deltaPercent', { pct: Math.round((totalDeltas / totalHits) * 100) })}
                  </Typography>
                  <Box sx={{ display: 'flex', height: 24, borderRadius: 1, overflow: 'hidden', mt: 0.5 }}>
                    <Box sx={{ width: `${(totalAlphas / totalHits) * 100}%`, bgcolor: '#4caf50' }} />
                    <Box sx={{ width: `${(totalCharlies / totalHits) * 100}%`, bgcolor: '#2196f3' }} />
                    <Box sx={{ width: `${(totalDeltas / totalHits) * 100}%`, bgcolor: '#ff9800' }} />
                  </Box>
                </Box>
              )}
            </Paper>
          )}

          {/* Stage Breakdown */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom>{t('results.stageBreakdown')}</Typography>
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ textAlign: 'start' }}>{t('results.stage')}</TableCell>
                    <TableCell align="center">A</TableCell>
                    <TableCell align="center">C</TableCell>
                    <TableCell align="center">D</TableCell>
                    <TableCell align="center">M</TableCell>
                    <TableCell align="center">NS</TableCell>
                    <TableCell align="center">Pen</TableCell>
                    <TableCell align="center">{t('results.time')}</TableCell>
                    <TableCell align="center">Total</TableCell>
                    <TableCell align="center">HF</TableCell>
                    <TableCell align="center">%</TableCell>
                    <TableCell align="center">{t('results.stagePoints')}</TableCell>
                    <TableCell align="center" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stages.map((stageItem, idx) => {
                    if (stageItem.inactive) return null;
                    const score = shooterStageScores[idx];
                    const orig = origShooterStageScores[idx];
                    const modified = isStageModified(idx);
                    if (!score) {
                      return (
                        <TableRow key={idx}>
                          <TableCell sx={{ fontWeight: 'bold', textAlign: 'start' }}>{t('results.stageN', { n: idx + 1 })}</TableCell>
                          <TableCell colSpan={12} align="center" sx={{ color: 'text.secondary' }}>
                            {t('results.noData')}
                          </TableCell>
                        </TableRow>
                      );
                    }
                    return (
                      <TableRow key={idx} sx={modified ? { bgcolor: '#fff3e0' } : undefined}>
                        <TableCell sx={{ fontWeight: 'bold', textAlign: 'start' }}>{t('results.stageN', { n: idx + 1 })}</TableCell>
                        <TableCell align="center">
                          {modified ? <StrikeThroughNewValue value={orig?.alphas} newValue={score.alphas} /> : score.alphas}
                        </TableCell>
                        <TableCell align="center">
                          {modified ? <StrikeThroughNewValue value={orig?.charlies} newValue={score.charlies} /> : score.charlies}
                        </TableCell>
                        <TableCell align="center">
                          {modified ? <StrikeThroughNewValue value={orig?.deltas} newValue={score.deltas} /> : score.deltas}
                        </TableCell>
                        <TableCell align="center" sx={{ color: score.misses > 0 ? 'error.main' : 'inherit' }}>
                          {modified ? <StrikeThroughNewValue value={orig?.misses} newValue={score.misses} /> : score.misses}
                        </TableCell>
                        <TableCell align="center" sx={{ color: score.noShoots > 0 ? 'error.main' : 'inherit' }}>
                          {modified ? <StrikeThroughNewValue value={orig?.noShoots} newValue={score.noShoots} /> : score.noShoots}
                        </TableCell>
                        <TableCell align="center" sx={{ color: score.penalties > 0 ? 'error.main' : 'inherit' }}>
                          {modified ? <StrikeThroughNewValue value={orig?.penalties} newValue={score.penalties} /> : score.penalties}
                        </TableCell>
                        <TableCell align="center">
                          {modified ? <StrikeThroughNewValue value={pad(orig?.time, 2)} newValue={pad(score.time, 2)} /> : pad(score.time, 2)}
                        </TableCell>
                        <TableCell align="center">
                          {modified ? <StrikeThroughNewValue value={orig?.total} newValue={score.total} /> : score.total}
                        </TableCell>
                        <TableCell align="center">
                          {modified ? <StrikeThroughNewValue value={pad(orig?.factor, 4)} newValue={pad(score.factor, 4)} /> : pad(score.factor, 4)}
                        </TableCell>
                        <TableCell align="center">
                          {modified
                            ? <StrikeThroughNewValue value={`${pad(orig?.percentage / 100, 2)}%`} newValue={`${pad(score.percentage / 100, 2)}%`} />
                            : `${pad(score.percentage / 100, 2)}%`
                          }
                        </TableCell>
                        <TableCell align="center">
                          {modified
                            ? <StrikeThroughNewValue value={pad(orig?.points, 4)} newValue={pad(score.points, 4)} />
                            : pad(score.points, 4)
                          }
                        </TableCell>
                        <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                          <Button
                            size="small"
                            variant={modified ? 'contained' : 'outlined'}
                            color={modified ? 'warning' : 'primary'}
                            sx={{ textTransform: 'none', minWidth: 0, px: 1, fontSize: 12 }}
                            onClick={() => setWhatIfStageIdx(idx)}
                          >
                            {t('results.whatIfButton')}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          </Paper>

          {/* Stage percentage visual */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom>{t('results.stagePerformance')}</Typography>
            {stages.map((stageItem, idx) => {
              if (stageItem.inactive) return null;
              const score = shooterStageScores[idx];
              const pct = score ? score.percentage / 100 : 0;

              return (
                <Box key={idx} sx={{ mb: 1.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2">{t('results.stageN', { n: idx + 1 })}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {score ? `${pad(pct, 2)}%` : '-'}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(pct, 100)}
                    sx={{
                      height: 12,
                      borderRadius: 1,
                      bgcolor: 'grey.200',
                      '& .MuiLinearProgress-bar': {
                        bgcolor: pct >= 95 ? '#4caf50' : pct >= 85 ? '#8bc34a' : pct >= 75 ? '#ffeb3b' : pct >= 60 ? '#ff9800' : '#f44336',
                        borderRadius: 1,
                      },
                    }}
                  />
                </Box>
              );
            })}
          </Paper>
        </>
      )}

      {/* What If Dialog */}
      {whatIfStageIdx !== null && currentRawScore && (
        <WhatIfDialog
          t={t}
          stageIndex={whatIfStageIdx}
          rawScore={currentRawScore}
          stage={stages[whatIfStageIdx]}
          scoreMode={match.scoreMode}
          onClose={() => setWhatIfStageIdx(null)}
          onReset={() => handleResetStage(whatIfStageIdx)}
          onChangeScore={(change) => handleChangeScore(whatIfStageIdx, change)}
        />
      )}

      {/* What If notification bar */}
      {hasWhatIf && selectedShooter && (
        <Paper
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            bgcolor: '#d32f2f',
            color: 'white',
            p: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            zIndex: 1300,
          }}
          elevation={8}
        >
          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
            {t('results.whatIfActive')}
          </Typography>
          <Link
            component="a"
            href={(() => {
              const params = new URLSearchParams();
              params.set('whatIf', btoa(unescape(encodeURIComponent(JSON.stringify(whatIfScores)))));
              params.set('shooterId', selectedShooter.id);
              if (hasOverride) {
                const override = { shooterId: selectedShooter.id };
                if (whatIfDivision) override.division = whatIfDivision;
                if (whatIfPowerFactor) override.powerFactor = whatIfPowerFactor;
                params.set('whatIfOverride', btoa(unescape(encodeURIComponent(JSON.stringify(override)))));
              }
              return `/${match.alias}/score-table?${params.toString()}`;
            })()}
            sx={{ color: 'white', textDecoration: 'underline', fontWeight: 'bold', fontSize: 14 }}
          >
            {t('results.whatIfViewScoreTable')}
          </Link>
          <Button
            size="small"
            variant="outlined"
            sx={{ color: 'white', borderColor: 'white', textTransform: 'none', ml: 1 }}
            onClick={() => { setWhatIfScores([]); setWhatIfStageIdx(null); setWhatIfDivision(null); setWhatIfPowerFactor(null); }}
          >
            {t('results.whatIfResetAll')}
          </Button>
        </Paper>
      )}

      {/* Spacer for fixed notification bar */}
      {hasWhatIf && selectedShooter && <Box sx={{ height: 60 }} />}
    </Box>
  );
}
